import classNames from 'classnames';
import styles from './VehicleDidHistory.module.css';
import { Table, ConfigProvider, Tooltip, Spin, Button, Modal, message } from 'antd';
import { ColumnsType } from 'antd/es/table';
import 'font-awesome/css/font-awesome.min.css';
import React, { useState, useEffect } from 'react';
import { getVehicleStatusModuleHistory } from 'src/app/query.service';
import axios from 'axios';

interface Data {
  [key: string]: any;
}

interface DidPacket {
  name: string;
  valueNumber: number;
  units: string;
  packetType: string;
  valueString?: string;
}

export interface DataType {
  key: string;
  packetValue: React.ReactNode;
  subPackets?: DidPacket[];
  isExpanded?: boolean;
  eventTimestamp: string;
  session_timestamp: string;
  sourceType: string;
  dataReadType: string;
  module: string;
  didHex: string;
  didDescription: string;
  didSize: string;
  didType: string;
  packetName: React.ReactNode;
  // packetValues: string;
  didValueHex: string;
  timezoneOffsetMin: string;
  totalDistanceMiles: string;
  totalDistanceKM: string;
  sourceFile: string;
  sessionID: string;
}

interface VehicleDidHistoryProps {
  searchedVin: string;
}
type FileType = '.zip' | '.csv';
type QueryType = 'session' | 'someOtherType';

const VehicleDidHistory: React.FC<VehicleDidHistoryProps> = ({ searchedVin }) => {
  const [apiData, setApiData] = useState<Data[]>([]);
  const [dataSource, setDataSource] = useState<Data[]>([]);
  const [expandedRows, setExpandedRows] = useState<React.Key[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [isDownloadPending, setIsDownloadPending] = useState(false);
  const [error, setError] = useState(null);
  const [selectedSession, setSelectedSession] = useState<Data | null>(null);
  const handleRowClick = (record: Data) => {
    setSelectedSession(record);
    setModalVisible(true);
  };

  const getDownloadUrl = (
    fileType: FileType,
    queryType: QueryType,
    // userId: string,
    offset?: number
  ): string => {
    let baseUrl = `${process.env.REACT_APP_API_URL}/viewer/${queryType}`;

    if (queryType === 'session') {
      baseUrl += fileType === '.zip' ? '/summary/export' : '/export';
    } else {
      baseUrl = `${process.env.REACT_APP_API_URL}/${queryType}/query/final/export`;
    }

    // Construct query parameters
    const params = new URLSearchParams();

    // if (userId) {
    //   params.append('userId', userId);
    // }

    if (offset !== undefined) {
      params.append('offset', offset.toString());
    }

    // Append query parameters if they exist
    return params.toString() ? `${baseUrl}?${params.toString()}` : baseUrl;
  };

  const convertToISOString = (timestamp: string): string => {
    // Split date and time
    const [datePart, timePart] = timestamp.split(', ');

    // Extract day, month, year
    const [day, month, year] = datePart.split('/').map(Number);

    // Construct a new Date object (months are zero-based in JS)
    const formattedDate = new Date(year, month - 1, day, ...timePart.split(':').map(Number));

    // Convert to ISO format and remove milliseconds
    return formattedDate.toISOString().split('.')[0] + 'Z';
  };

  const getSddSessionInfo = async (
    queryParameter: { sessionId: string; sessionTimestamp: string },
    // userId: string,
    token: string
  ): Promise<string> => {
    const downloadUrl = `${process.env.REACT_APP_API_URL}/viewer/session/info`;
    // if (userId) {
    //   downloadUrl += `?userId=${encodeURIComponent(userId)}`;
    // }

    const response = await axios.post(downloadUrl, queryParameter, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      responseType: 'text',
    });

    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to fetch session info');
    }
  };

  const handleDownloadZip = async () => {
    if (!selectedSession) return;

    // const userId: string = localStorage.getItem('userId') || '';
    const token: string | null = localStorage.getItem('id_token');
    const isoTimestamp = convertToISOString(selectedSession.session_timestamp);

    if (!token) {
      console.error('No auth token found!');
      return;
    }

    if (isDownloadPending) return;
    setIsDownloadPending(true);

    try {
      const downloadUrl: string = getDownloadUrl('.zip', 'session');

      const payload = {
        sessionId: selectedSession.sessionId,
        sessionTimestamp: isoTimestamp,
      };

      const response = await axios.post(downloadUrl, payload, {
        responseType: 'blob', // Ensures the response is treated as a file
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      let fileName = 'download.zip';
      const contentDisposition = response.headers['content-disposition'];

      if (contentDisposition) {
        console.log('Content-Disposition Header:', contentDisposition); // Debugging

        const filenameRegex = /filename=["']?([^"';]+)["']?/;
        const matches = contentDisposition.match(filenameRegex);

        if (matches && matches[1]) {
          fileName = matches[1]; // Correctly extracts "example.zip"
        }
      }

      // Handle file download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();

      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Error downloading the file:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsDownloadPending(false);
    }
  };
  const handleViewSession = async () => {
    if (!selectedSession) return;

    setIsLoading(true);

    // const userId: string = decodeURIComponent(localStorage.getItem('userId')) || '';
    const token: string | null = localStorage.getItem('id_token');
    const isoTimestamp = convertToISOString(selectedSession.session_timestamp);
    if (!token) {
      console.error('No auth token found!');
      setIsLoading(false);
      return;
    }

    const queryParameter = {
      sessionId: selectedSession.sessionId,
      sessionTimestamp: isoTimestamp,
    };

    try {
      const sddSummaryHTML = await getSddSessionInfo(queryParameter, token);

      // Open a new window and display the session info
      const windowRef = window.open('/', '_blank', 'scrollbars,resizable,height=800,width=1325');
      if (windowRef) {
        const sessionViewerPlaceholder = `<div style='overflow-y:auto;overflow-x:hidden;height:100%;width:100%'>${sddSummaryHTML}</div>`;
        windowRef.document.write(sessionViewerPlaceholder);
      }
    } catch (err) {
      console.error('err fetching session info:', err);
      setError(err instanceof Error ? err.message : 'An err occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const columns: ColumnsType<Data> = [
    {
      title: 'Event Date/Time',
      dataIndex: 'eventTimestamp',
      key: 'eventTimestamp',
      fixed: 'left',
      width: '100px',
      onHeaderCell: () => {
        return { style: { minWidth: '100px' } }; // Set min-width for the column
      },
    },
    {
      title: 'Session Date/Time',
      dataIndex: 'session_timestamp',
      key: 'session_timestamp',
      width: '100px',
      onHeaderCell: () => {
        return { style: { minWidth: '100px' } }; // Set min-width for the column
      },
    },
    {
      title: 'Source Type',
      dataIndex: 'sourceType',
      key: 'sourceType',
      width: '100px',
      onHeaderCell: () => {
        return { style: { minWidth: '100px' } }; // Set min-width for the column
      },
    },
    {
      title: 'Data Read Type',
      dataIndex: 'dataReadType',
      key: 'dataReadType',
      width: '100px',
      onHeaderCell: () => {
        return { style: { minWidth: '100px' } }; // Set min-width for the column
      },
    },
    {
      title: 'Module',
      dataIndex: 'module',
      key: 'module',
      width: '100px',
      onHeaderCell: () => {
        return { style: { minWidth: '100px' } }; // Set min-width for the column
      },
    },
    {
      title: 'DID (hex)',
      dataIndex: 'didHex',
      key: 'didHex',
      width: '100px',
      onHeaderCell: () => {
        return { style: { minWidth: '100px' } }; // Set min-width for the column
      },
    },
    {
      title: 'DID Description',
      dataIndex: 'didDescription',
      key: 'didDescription',
      width: '100px',
      onHeaderCell: () => {
        return { style: { minWidth: '100px' } }; // Set min-width for the column
      },
    },
    {
      title: 'DID Size',
      dataIndex: 'didSize',
      key: 'didSize',
      width: '100px',
      onHeaderCell: () => {
        return { style: { minWidth: '100px' } }; // Set min-width for the column
      },
    },
    {
      title: 'DID Type',
      dataIndex: 'didType',
      key: 'didType',
      width: '100px',
      onHeaderCell: () => {
        return { style: { minWidth: '100px' } }; // Set min-width for the column
      },
    },
    {
      title: 'Packet Name',
      key: 'packetName',
      width: 500,
      render: (text, record) => {
        return <>{record.packetName}</>;
      },
    },
    {
      title: 'Packet Values',
      key: 'packetValue',
      width: 500,
      render: (text, record) => {
        return <>{record.packetValue}</>;
      },
    },
    {
      title: 'DID Value (hex)',
      dataIndex: 'didValue',
      key: 'didValue',
      width: 1500,
      onHeaderCell: () => {
        return { style: { minWidth: '1500px' } }; // Set min-width for the column
      },
    },
    {
      title: 'Timezone Offset Min',
      dataIndex: 'timezoneOffsetMinutes',
      key: 'timezoneOffsetMinutes',
      width: 100,
      onHeaderCell: () => {
        return { style: { minWidth: '100px' } }; // Set min-width for the column
      },
    },
    {
      title: 'Total Distance Miles',
      dataIndex: 'odoMiles',
      key: 'odoMiles',
      width: 100,
      onHeaderCell: () => {
        return { style: { minWidth: '100px' } }; // Set min-width for the column
      },
    },
    {
      title: 'Total Distance KM',
      dataIndex: 'odoKm',
      key: 'odoKm',
      width: 100,
      onHeaderCell: () => {
        return { style: { minWidth: '100px' } }; // Set min-width for the column
      },
    },
    {
      title: 'Source File',
      dataIndex: 'sourceFile',
      key: 'sourceFile',
      width: 600,
      onHeaderCell: () => {
        return { style: { minWidth: '900px' } }; // Set min-width for the column
      },
    },
    {
      title: 'Session ID',
      dataIndex: 'sessionId',
      key: 'sessionId',
      width: 600,
      onHeaderCell: () => {
        return { style: { minWidth: '900px' } }; // Set min-width for the column
      },
    },
  ];
  const getVehicleDidHistory = async () => {
    const userId = localStorage.getItem('userId');
    try {
      setIsLoading(true);
      const result: any = await getVehicleStatusModuleHistory('did', searchedVin);
      const sortedData = result.items.sort((a: Data, b: Data) => {
        return new Date(b.eventTimestamp).getTime() - new Date(a.eventTimestamp).getTime();
      });

      setApiData([...sortedData]);
    } catch (err) {
      setIsLoading(false);
      console.log('error...', err);
    }
  };
  const handleExpandClick = (record: Data) => {
    setExpandedRows((prev) =>
      prev.includes(record.key) ? prev.filter((key) => key !== record.key) : [...prev, record.key]
    );
  };

  const renderExpandedContent = (record: DataType) => {
    if (!expandedRows.includes(record.key) || !record.subPackets) return null;

    const isExpandable = record.didType !== 'single_value' && record.subPackets.length > 1;

    return (
      <div>
        {/* Keep Expand Icon and First SubPacket in Same Line */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 0px' }}>
          {isExpandable && (
            <span
              style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
              onClick={() => handleExpandClick(record)}
            >
              {expandedRows.includes(record.key) ? (
                <></>
              ) : (
                <i className="fa fa-caret-right" title="Show Additional Packages"></i>
              )}
            </span>
          )}
          <div style={{ flexGrow: 1, display: 'flex', alignItems: 'center', gap: '10px' }}></div>
        </div>

        {/* Render Remaining SubPackets */}
        {record.subPackets.slice(1, 6).map((packet, index) => (
          <div
            key={index}
            style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0px' }}
          >
            <div>
              {packet.name}
              {index === 4 && (
                <Tooltip title="More items available">
                  <span> ...</span>
                </Tooltip>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderExpandedContent2 = (record: DataType) => {
    if (!expandedRows.includes(record.key) || !record.subPackets) return null;
    const isExpandable = record.didType !== 'single_value' && record.subPackets.length > 1;
    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 0px' }}>
          <span style={{ flexGrow: 1, display: 'flex', alignItems: 'center', gap: '10px' }}>
            {record.subPackets.length > 0 && <span></span>}
          </span>
          {isExpandable && (
            <span
              style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
              onClick={() => handleExpandClick(record)}
            >
              {expandedRows.includes(record.key) ? (
                <></>
              ) : (
                <i className="fa fa-caret-right" title="Show Additional Packages"></i>
              )}
            </span>
          )}
        </div>

        {/* Render Remaining SubPackets */}
        {record.subPackets.slice(1, 6).map((packet, index) => (
          <div
            key={index}
            style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0px' }}
          >
            <div>
              {`${packet.valueNumber} ${packet.units} (${packet.packetType})`}
              {index === 4 && (
                <Tooltip title="More items available">
                  <span> ...</span>
                </Tooltip>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };
  useEffect(() => {
    getVehicleDidHistory();
  }, []);
  useEffect(() => {
    let lastCopied = '';
    let messageVisible = false;
    let hideTimeout: NodeJS.Timeout;

    const handleMouseUp = () => {
      const selectedText = window.getSelection()?.toString().trim();

      if (selectedText && selectedText !== lastCopied) {
        lastCopied = selectedText;

        navigator.clipboard
          .writeText(selectedText)
          .then(() => {
            if (!messageVisible) {
              messageVisible = true;
              message.destroy(); // remove any lingering messages
              message.success('Copied to clipboard', 1.2);

              hideTimeout = setTimeout(() => {
                messageVisible = false;
              }, 1200); // match toast duration
            }
          })
          .catch((err) => {
            console.error('Clipboard copy failed:', err);
            message.destroy();
            message.error('Failed to copy');
          });
      }
    };

    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mouseup', handleMouseUp);
      clearTimeout(hideTimeout);
    };
  }, []);
  useEffect(() => {
    if (apiData && apiData.length > 0) {
      const dataSource1 = (apiData || []).map((item, index) => {
        const didPackets: Data[] = JSON.parse(item.didPackets);
        const subPackets = (didPackets || []).map((packet) => ({
          name: packet.name,
          valueNumber: packet.valueNumber,
          units: packet.units,
          packetType: packet.packetType,
          valueString: packet.valueString,
        }));

        let packetName = '';
        let packetValue = '';

        if (item.didType === 'single_value' && didPackets.length > 0) {
          const singlePacket = didPackets[0];
          packetName = singlePacket.name;
          packetValue = `${singlePacket.valueString || singlePacket.valueNumber} ${
            singlePacket.units
          } (${singlePacket.packetType})`;
        } else if (didPackets && didPackets.length > 0) {
          packetName = didPackets[0].name;
          packetValue = `${didPackets[0].valueNumber} ${didPackets[0].units} (${didPackets[0].packetType})`;
        }

        return {
          key: `${index}`,
          eventTimestamp: new Date(item.eventTimestamp).toLocaleString(),
          session_timestamp: new Date(item.session_timestamp).toLocaleString(),
          sourceType: item.sourceType,
          dataReadType: item.dataReadType,
          module: item.module,
          didHex: item.didId || 'XXXX',
          didDescription: item.didDescription,
          didSize: item.didSize,
          didType: item.didType,
          packetName,
          packetValue,
          subPackets,
          didValue: item.didValue,
          timezoneOffsetMinutes: item.timezoneOffsetMinutes,
          odoMiles: item.odoMiles,
          odoKm: item.odoKm,
          sourceFile: item.sourceFile,
          sessionId: item.sessionId,
        };
      });
      const tableData = dataSource1.map((record: any, index) => {
        const isExpandable =
          record.didType !== 'single_value' && record.subPackets && record.subPackets.length > 1;
        return {
          ...record,
          packetName: (
            <>
              <div>
                {record.packetName}
                {isExpandable && (
                  <span style={{ marginLeft: 15 }} onClick={() => handleExpandClick(record)}>
                    {expandedRows.includes(record.key) ? (
                      <i className="fa fa-caret-down" title="Hide Additional Packages"></i>
                    ) : (
                      <i className="fa fa-caret-left" title="Show Additional Packages"></i>
                    )}
                  </span>
                )}
              </div>
              {renderExpandedContent(record)}
            </>
          ),
          packetValue: (
            <>
              <div>
                {record.packetValue}
                {isExpandable && (
                  <span style={{ marginLeft: 15 }} onClick={() => handleExpandClick(record)}>
                    {expandedRows.includes(record.key) ? (
                      <>
                        <i className="fa fa-caret-down" title="Hide Additional Packages"></i>
                      </>
                    ) : (
                      <i className="fa fa-caret-left" title="Show Additional Packages"></i>
                    )}
                  </span>
                )}
              </div>
              {renderExpandedContent2(record)}
            </>
          ),
        };
      });
      setDataSource(tableData);
      setIsLoading(false);
    }
  }, [apiData, expandedRows]);
  const tableTheme = {
    components: {
      Table: {
        headerBg: '#dcdcdc',
        headerSplitColor: 'white',
        headerBorderRadius: 0,
        cellFontSize: 14,
      },
    },
  };
  return (
    <div className={classNames(styles.VehicleDidHistory)}>
      <ConfigProvider theme={tableTheme}>
        <Table
          columns={columns}
          dataSource={dataSource}
          scroll={{ x: 'max-content', y: 300 }}
          pagination={false}
          rowKey="id"
          rowClassName={(record) => (expandedRows.includes(record.key) ? 'expanded-row' : '')}
          loading={{
            spinning: isLoading,
            indicator: <Spin className={classNames(styles['loader-color'])} />,
          }}
          onRow={(record) => ({
            onClick: (event) => {
              const target = event.target as HTMLElement;

              // Prevent modal trigger if user is selecting text
              const selection = window.getSelection();
              if (selection && selection.toString().length > 0) {
                return; // user is selecting text, don't open modal
              }

              // Prevent row click from expand toggle/caret/icon
              if (
                target.classList.contains('ant-table-row-expand-icon') ||
                target.closest('.ant-table-row-expand-icon-cell') ||
                target.classList.contains('fa-caret-down') ||
                target.classList.contains('fa-caret-left') ||
                target.closest('.ignore-row-click')
              ) {
                return;
              }

              handleRowClick(record);
            },
          })}
        />
      </ConfigProvider>
      <Modal
        title="Session"
        visible={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={600}
      >
        {selectedSession && (
          <div>
            <p>{selectedSession.sessionId}</p>
            {selectedSession.sourceType == 'Topix' ? (
              <div className={classNames(styles['VehicleDidHistory--topix-notes-container'])}>
                <p>
                  TOPIX data cannot currently be downloaded in VFDB, please use{' '}
                  <a href="https://topix.jaguar.jlrext.com" target="_blank" rel="noreferrer">
                    topix.jaguar.jlrext.com
                  </a>
                </p>
              </div>
            ) : (
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 16 }}>
                <Button
                  onClick={handleViewSession}
                  className={classNames(styles['VehicleDidHistory--Buttons'])}
                >
                  View Session
                </Button>
                <Button
                  onClick={handleDownloadZip}
                  className={classNames(styles['VehicleDidHistory--Buttons'])}
                  style={{ marginRight: 20 }}
                >
                  Download ZIP
                </Button>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default VehicleDidHistory;
